import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import authAtom from '~/recoil/atom/auth';
import containersSelector from '~/recoil/selectors/container';

import { PLATFORM_ID } from '~/globalVariables';

import { platformCustomerListener } from '~/firebase/listeners/platform';
import { SnapshotFn } from '~/firebase/listeners/type';

import { Customer } from '~/types/platform/customer';

type CustomerContainerSummarize = {
  id: string;
  name: string;
  expiredDate: string;
  count: number;
  amount: number;
};

export default function useContainers() {
  const auth = useRecoilValue(authAtom);
  const containersInfo = useRecoilValue(containersSelector);
  const [customerContainers, setCustomerContainers] = useState<
    Map<string, CustomerContainerSummarize>
  >(new Map());

  const handleCustomerListener: SnapshotFn = async (doc) => {
    if (doc.exists()) {
      const { containers } = doc.data() as Customer;
      const newCustomerContainers = Object.entries(containers).reduce<
        Map<string, CustomerContainerSummarize>
      >((prev, [containerId, details]) => {
        const containerInfo = containersInfo[containerId];
        const previousContainerInfo = customerContainers.get(containerId);
        const newContainer = Object.entries(details).reduce(
          (prev, [date, amountOfDate]) => ({
            ...prev,
            // comparing string to find closest date. eg. '2021-12-11' > '2021-12-10'
            expiredDate: prev.expiredDate && prev.expiredDate < date ? prev.expiredDate : date,
            amount: prev.amount + amountOfDate,
          }),
          {
            name: containerInfo?.name || '餐盒',
            expiredDate: '',
            count: 0,
            amount: 0,
          },
        );
        prev.set(containerId, {
          ...newContainer,
          id: containerId,
          count:
            previousContainerInfo && previousContainerInfo.count <= newContainer.amount
              ? previousContainerInfo.count
              : newContainer.amount,
        });
        return prev;
      }, new Map());
      setCustomerContainers(newCustomerContainers);
    } else {
      setCustomerContainers(new Map());
    }
  };

  const handleUpdateCount = (id: string, count: number) => {
    const container = customerContainers.get(id);
    if (!container || count < 0 || container.amount < count) {
      return;
    }
    const newContainerMap = new Map(customerContainers);
    setCustomerContainers(newContainerMap.set(id, { ...container, count: count }));
  };

  useEffect(() => {
    if (auth) {
      const unlisten = platformCustomerListener(PLATFORM_ID, auth.uid, handleCustomerListener);
      return () => {
        unlisten();
      };
    }
  }, [auth, containersInfo]);

  return {
    customerContainers: [...customerContainers.values()].filter(({ amount }) => amount),
    handleUpdateCount,
  };
}
